import React, { useState } from 'react';
import {connect} from 'react-redux';
import { useTranslation } from "react-i18next";

import ToolBarAction from '../top-bar/toolbar-action';
import { getSelectedFolder } from '../../selectors/folders';
import { getCredentials } from '../../selectors/application';
import { FolderTypes, findTrashFolder } from '../../services/folder';
import { clearSelectedMessage} from '../../services/application';
import {
  deleteAllFolderMessages,
  moveMessages,
  deleteMessages,
  setMessagesSeen
} from '../../services/message';
import { setFilteredMessages } from '../../actions/application';
import ConfirmDeleteFromTrashDialog from '../top-bar/confirm-delete-from-trash-dialog';
import ConfirmClearFolderDialog from '../top-bar/confirm-clear-folder-dialog';
import { Confirmation } from '../../components/notification/confirmation';
import ArchiveIcon from '../../assets/icons/archive.svg?react';

const MessageNotFound = ({
  selectedMessagesAllUnread,
  selectedMessages,
  onDeleteClick,
  selectedFolder,
  clearFolder,
  onSetMessagesSeen,
  selectedMessagesClassification,
  onRefreshClick,
  application
}) => {
  const { t } = useTranslation();
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [deletingFromTrash, setDeletingFromTrash] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const isTrash = selectedFolder && selectedFolder.type === FolderTypes.TRASH;

  const selectedMessagesCounter = selectedMessages.length;

  const handleReadUnread = () => {
    if (selectedMessages.length === 0) {
      return;
    }

    const hasFilters =
      application &&
      application.filtersApplied &&
      Object.keys(application.filtersApplied).length > 0;

    let nextSeen = selectedMessagesAllUnread;
    if (hasFilters && Array.isArray(application.filteredMessages)) {
      const selectedUidSet = new Set(
        selectedMessages
          .map(m => {
            if (!m) {
              return null;
            }
            if (typeof m.uid === 'undefined' || m.uid === null) {
              return null;
            }
            return String(m.uid);
          })
          .filter(v => v !== null)
      );

      const selectedMessageIdSet = new Set(
        selectedMessages
          .map(m => {
            if (!m) {
              return null;
            }
            if (typeof m.messageId === 'undefined' || m.messageId === null) {
              return null;
            }
            return String(m.messageId);
          })
          .filter(v => v !== null)
      );

      const selectedFiltered = application.filteredMessages.filter(item => {
        const md = item && item.metadata ? item.metadata : {};
        let idCandidate = null;
        if (typeof md.idExternal !== 'undefined' && md.idExternal !== null) {
          idCandidate = String(md.idExternal);
        } else if (typeof md.id !== 'undefined' && md.id !== null) {
          idCandidate = String(md.id);
        }
        let msgIdCandidate = null;
        if (typeof md.messageId !== 'undefined' && md.messageId !== null) {
          msgIdCandidate = String(md.messageId);
        }
        const matchesUid =
          idCandidate !== null && selectedUidSet.has(idCandidate);
        const matchesMsgId =
          msgIdCandidate !== null && selectedMessageIdSet.has(msgIdCandidate);
        return matchesUid || matchesMsgId;
      });

      const anySeen = selectedFiltered.some(
        item => !!(item && item.metadata && item.metadata.seen)
      );
      const allUnread = selectedFiltered.length > 0 && !anySeen;
      nextSeen = allUnread;
    }

    onSetMessagesSeen(nextSeen);
  };

  const onDelete = action => {
    const existIdActuation = selectedMessagesClassification.some(
      x => x.idActuation
    );
    if (existIdActuation) {
      setShowRemoveConfirmation(true);
      return;
    }
    if (isTrash) {
      setDeletingFromTrash(true);
    } else {
      // Uncheck messages from Lexon
      for (let i = 0; i < selectedMessages.length; i++) {
        window.dispatchEvent(
          new CustomEvent('RemoveSelectedDocument', {
            detail: {
              ...selectedMessages[i],
              id: selectedMessages[i].messageId
            }
          })
        );
      }
      // Refresh once the server has finished the operation instead of using a fixed delay
      Promise.resolve(action()).finally(() => {
        onRefreshClick();
      });
    }
  };

  const onRemoveMessage = deleteMessagesActions => {
    if (isTrash) {
      setDeletingFromTrash(true);
    } else {
      // Uncheck messages from Lexon
      for (let i = 0; i < selectedMessages.length; i++) {
        window.dispatchEvent(
          new CustomEvent('RemoveSelectedDocument', {
            detail: {
              ...selectedMessages[i],
              id: selectedMessages[i].messageId
            }
          })
        );
      }
      Promise.resolve(deleteMessagesActions()).finally(() => {
        onRefreshClick();
      });
    }
    setShowRemoveConfirmation(false);
  };

  return (
    <div
      className="
      message-not-found-wrapper
      d-flex
      flex-column
      align-items-center
      justify-content-center
    "
    >
      <div className="message-not-found d-flex align-items-center">
        <ArchiveIcon fill="var(--gray-blue)" />
        <div className="ml-2">
          <p className="inter-medium-14 text-color-grey-600">
            {t('message-not-found.selected_message')}
          </p>
          <p className="text-color-grey-600 inter-regular-12">
            {t('message-not-found.content_here')}
          </p>
        </div>
      </div>
      {selectedMessages.length >= 2 && (
        <React.Fragment>
          {!isTrash && (
            <div className="px-2 my-2">
              <ToolBarAction
                onClick={() => {
                  onDelete(onDeleteClick);
                }}
                icon="lf-icon-trash"
                title={t('message-not-found.move-to-trash')}
                disabled={selectedMessagesCounter === 0}
                hideText={false}
              />
            </div>
          )}
          {isTrash && (
            <div className="px-2 my-2">
              <ToolBarAction
                onClick={() => setEmptyTrashDialogOpen(true)}
                icon="lf-icon-trash"
                title={t('message-toolbar.empty-trash')}
                hideText={false}
              />
            </div>
          )}
          <div className="px-2">
            <ToolBarAction
              onClick={handleReadUnread}
              icon="lf-icon-mail-open"
              title={t('message-not-found.read-unread')}
              disabled={selectedMessagesCounter === 0}
              hideText={false}
            />
          </div>
          <ConfirmDeleteFromTrashDialog
            visible={deletingFromTrash}
            deleteAction={() => {
              onDeleteClick();
              setDeletingFromTrash(false);
            }}
            cancelAction={() => setDeletingFromTrash(false)}
          />
          <Confirmation
            initialModalState={showRemoveConfirmation}
            onAccept={() => {
              onRemoveMessage(onDeleteClick);
            }}
            onCancel={() => {
              setShowRemoveConfirmation(false);
            }}
            message={t('topBar.remove-message-confirmation')}
            titleAccept="Si"
            titleCancel="No"
            iconType="lf-icon lf-icon-information"
          />
          <ConfirmClearFolderDialog
            visible={emptyTrashDialogOpen}
            cancelAction={() => setEmptyTrashDialogOpen(false)}
            deleteAction={() => {
              clearFolder();
              setEmptyTrashDialogOpen(false);
            }}
          />
        </React.Fragment>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const useFilteredMessages =
    state.application.filtersApplied &&
    Object.keys(state.application.filtersApplied).length > 0;
  const selectedMessagesIds = state.messages.selected;
  const messages =
     state.application.selectedFolderId &&
    state.messages.cache[state.application.selectedFolderId]
      ? Array.from(
        Object.values(state.messages.cache[state.application.selectedFolderId] || {})
      )
      : [];
  const selectedMessages = useFilteredMessages
    ? selectedMessagesIds
    : messages.filter(m =>
      selectedMessagesIds.find(mIds => mIds.messageId === m.messageId)
    );

  const selectedMessagesAllUnread =
    selectedMessages.filter(m => m.seen === true).length === 0;

  return {
    credentials: getCredentials(state),
    selectedFolder: getSelectedFolder(state) || {},
    application: state.application,
    folders: state.folders,
    selectedMessages: selectedMessages,
    selectedMessagesAllUnread: selectedMessagesAllUnread,
    selectedMessagesClassification: state.messages.selectedMessages,
    lexon: state.lexon
  };
};

const mapDispatchToProps = dispatch => ({
  clearFolder: (credentials, folder) =>
    deleteAllFolderMessages(dispatch, credentials, folder),
  onDeleteClick: (
    credentials,
    folders,
    selectedFolder,
    selectedMessages,
    user,
    lexon,
    application
  ) => {
    const trashFolder = findTrashFolder(folders);
    if (!trashFolder || !selectedMessages || selectedMessages.length === 0) {
      return;
    }

    const exploded =
      folders && folders.explodedItems ? folders.explodedItems : {};
    const groups = {};
    const pending = [];
    selectedMessages.forEach(m => {
      const folderId =
        m.folderId || (selectedFolder && selectedFolder.folderId);
      if (!folderId) {
        return;
      }
      if (!groups[folderId]) {
        groups[folderId] = [];
      }
      groups[folderId].push(m);
    });

    Object.keys(groups).forEach(folderId => {
      const inSelectedFolder =
        selectedFolder && selectedFolder.folderId === folderId;
      let fromFolder = null;
      if (exploded && exploded[folderId]) {
        fromFolder = exploded[folderId];
      } else if (inSelectedFolder) {
        fromFolder = selectedFolder;
      }
      if (!fromFolder) {
        return;
      }

      if (fromFolder.folderId === trashFolder.folderId) {
        pending.push(deleteMessages(
          dispatch,
          credentials,
          fromFolder,
          groups[folderId],
          lexon
        ));
      } else {
        pending.push(moveMessages(
          dispatch,
          credentials,
          fromFolder,
          trashFolder,
          groups[folderId],
          user,
          lexon
        ));
      }
    });

    // Remove deleted items from filtered list, if applicable
    const hasFilters =
      application &&
      application.filtersApplied &&
      Object.keys(application.filtersApplied).length > 0;
    const filteredList =
      hasFilters && Array.isArray(application.filteredMessages)
        ? application.filteredMessages
        : [];
    if (hasFilters && filteredList.length > 0) {
      const uidSet = new Set(
        selectedMessages
          .map(m => {
            if (typeof m.uid === 'undefined' || m.uid === null) {
              return null;
            }
            return String(m.uid);
          })
          .filter(v => v !== null)
      );
      const msgIdSet = new Set(
        selectedMessages
          .map(m => {
            if (typeof m.messageId === 'undefined' || m.messageId === null) {
              return null;
            }
            return String(m.messageId);
          })
          .filter(v => v !== null)
      );

      const updated = filteredList.filter(item => {
        const md = item && item.metadata ? item.metadata : {};
        let idCandidate = null;
        if (typeof md.idExternal !== 'undefined' && md.idExternal !== null) {
          idCandidate = String(md.idExternal);
        } else if (typeof md.id !== 'undefined' && md.id !== null) {
          idCandidate = String(md.id);
        }
        const msgIdCandidate =
          typeof md.messageId !== 'undefined' && md.messageId !== null
            ? String(md.messageId)
            : null;
        const matchesUid = idCandidate !== null && uidSet.has(idCandidate);
        const matchesMsgId =
          msgIdCandidate !== null && msgIdSet.has(msgIdCandidate);
        return !(matchesUid || matchesMsgId);
      });
      dispatch(setFilteredMessages(updated));
    }
    clearSelectedMessage(dispatch);
    return Promise.all(pending);
  },
  onSetMessagesSeen: (
    credentials,
    folders,
    selectedFolder,
    selectedMessages,
    seen,
    filteredMessages,
    filtersApplied
  ) => {
    if (!selectedMessages || selectedMessages.length === 0) {
      return;
    }

    const exploded =
      folders && folders.explodedItems ? folders.explodedItems : {};
    const groups = {};

    selectedMessages.forEach(m => {
      const folderId =
        m.folderId || (selectedFolder && selectedFolder.folderId);
      if (!folderId) {
        return;
      }
      if (!groups[folderId]) {
        groups[folderId] = [];
      }
      groups[folderId].push(m);
    });

    Object.keys(groups).forEach(folderId => {
      const inSelectedFolder =
        selectedFolder && selectedFolder.folderId === folderId;
      let folderObj = null;
      if (exploded && exploded[folderId]) {
        folderObj = exploded[folderId];
      } else if (inSelectedFolder) {
        folderObj = selectedFolder;
      }
      if (!folderObj) {
        return;
      }
      setMessagesSeen(dispatch, credentials, folderObj, groups[folderId], seen);
    });

    // If in filtered mode, update filtered list items' seen flag locally
    const hasFilters = filtersApplied && Object.keys(filtersApplied).length > 0;
    if (
      hasFilters &&
      Array.isArray(filteredMessages) &&
      filteredMessages.length > 0
    ) {
      const selectedUidSet = new Set(
        selectedMessages
          .map(m => {
            if (!m) {
              return null;
            }
            if (typeof m.uid === 'undefined' || m.uid === null) {
              return null;
            }
            return String(m.uid);
          })
          .filter(v => v !== null)
      );

      const selectedMessageIdSet = new Set(
        selectedMessages
          .map(m => {
            if (!m) {
              return null;
            }
            if (typeof m.messageId === 'undefined' || m.messageId === null) {
              return null;
            }
            return String(m.messageId);
          })
          .filter(v => v !== null)
      );

      const updated = filteredMessages.map(item => {
        const md = item && item.metadata ? item.metadata : {};
        let idCandidate = null;
        if (typeof md.idExternal !== 'undefined' && md.idExternal !== null) {
          idCandidate = String(md.idExternal);
        } else if (typeof md.id !== 'undefined' && md.id !== null) {
          idCandidate = String(md.id);
        }
        let msgIdCandidate = null;
        if (typeof md.messageId !== 'undefined' && md.messageId !== null) {
          msgIdCandidate = String(md.messageId);
        }

        const matchesUid =
          idCandidate !== null && selectedUidSet.has(idCandidate);
        const matchesMsgId =
          msgIdCandidate !== null && selectedMessageIdSet.has(msgIdCandidate);

        if (matchesUid || matchesMsgId) {
          return {
            ...item,
            metadata: {
              ...md,
              seen: !!seen
            }
          };
        }
        return item;
      });
      dispatch(setFilteredMessages(updated));
    }
  }
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, stateProps, dispatchProps, ownProps, {
    clearFolder: () =>
      dispatchProps.clearFolder(
        stateProps.credentials,
        stateProps.selectedFolder
      ),
    onDeleteClick: () =>
      dispatchProps.onDeleteClick(
        stateProps.credentials,
        stateProps.folders,
        stateProps.selectedFolder,
        stateProps.selectedMessages,
        stateProps.application.user,
        stateProps.lexon,
        stateProps.application
      ),
    onSetMessagesSeen: seen =>
      dispatchProps.onSetMessagesSeen(
        stateProps.credentials,
        stateProps.folders,
        stateProps.selectedFolder,
        stateProps.selectedMessages,
        seen,
        stateProps.application.filteredMessages,
        stateProps.application.filtersApplied
      )
  });

const ConnectedMessageNotFound = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(MessageNotFound);

export default ConnectedMessageNotFound;
